import {isNotNil} from 'ramda';

import {Nullish} from 'shared/web';

export type BranchSelection = {kind: 'all'} | {kind: 'specific'; branchId: string} | {kind: 'none'};

interface ResolveBranchSelectionArgs {
  formBranch: string | string[] | Nullish;
  hasUserSelectedAll: boolean;
  hasInitialSeedFired: boolean;
}

const pickFirstBranch = (value: string | string[] | Nullish): string | Nullish =>
  Array.isArray(value) ? value[0] : value;

/**
 * Resolves what the form has actually committed for the branch filter.
 *
 * Assumes any non-nullish branch value in the form is a legitimate id — the
 * writers (`handleBranchChange` and the `useOnMount` seed) guarantee that.
 *
 * Note: `activeBranchId` is intentionally NOT consulted here. When the result
 * is `{kind: 'none'}`, the component uses `activeBranchId` as a cosmetic
 * display fallback and runs the `useOnMount` seed. Folding it in here would
 * make `kind === 'none'` unreachable and silently disable that seed.
 *
 * Once `hasInitialSeedFired` is true (set inside the mount-time setTimeout),
 * any subsequent undefined form branch (e.g. after a filter reset) resolves to
 * `{kind: 'all'}` rather than re-triggering the seed.
 */
export function resolveBranchSelection(args: ResolveBranchSelectionArgs): BranchSelection {
  const formBranchId = pickFirstBranch(args.formBranch);

  if (isNotNil(formBranchId)) {
    return {kind: 'specific', branchId: formBranchId};
  }

  if (args.hasUserSelectedAll || args.hasInitialSeedFired) {
    return {kind: 'all'};
  }

  return {kind: 'none'};
}
